import { togaApiRequest } from "../../../api/toga";
import { useUserStore, assignRole } from "../../../stores/useUserStore";
import { User } from "../../../hooks/useUserContext";

// import { createBrowserHistory } from "history";

interface UserProps {
	firstName: string;
	lastName: string;
	isUserLoggedIn: boolean;
}

interface OptionsType {
	transactionId?: string;
}

interface ExtendedOptionsType extends OptionsType {
	fields?: {
		[key: string]: string[];
	};
}

interface SamlData {
	auth: string;
	payload: any;
}

type SetUserFunc = (user: UserProps) => void;
const storedHostName = localStorage.getItem("hostName");

export async function submitLoginPassword(
	email: string,
	password: string,
	setUser: SetUserFunc,
	navigate: (to: string) => void,
): Promise<boolean> {
	// const history = createBrowserHistory();
	const payload = {
		email: email,
		password: password,
	};

	// make an auth request, also asking for top-level user information
	const response = await togaApiRequest("POST", "/auth/login", payload, {
		fields: [{ user: ["firstName", "lastName"] }],
	} as OptionsType);
	if (response.isSuccess) {
		const accessToken = response.data.tokens.access;
		const refreshToken = response.data.tokens.refresh;
		const firstName = response.data.user.firstName;
		const lastName = response.data.user.lastName;

		localStorage.setItem("accessToken", accessToken);
		localStorage.setItem("refreshToken", refreshToken);

		let fields = [];

		if (
			storedHostName === "COMPASS" ||
			storedHostName === "COMPASSCANADA"
		) {
			fields = ["_isSupervisor", "_isAdmin", "uuid"];
		} else if (storedHostName === "QUAD") {
			fields = ["_isGlobalAdmin", "_isBuyer", "_isItShopper", "uuid"];
		} else {
			fields = ["uuid"];
		}

		//Make an api call for Users to get the user details and save it in the state
		const userOptions = {
			fields: fields,
			where: {
				and: [{ "Users.email": { "=": email } }],
			},
		};
		const userData = await togaApiRequest(
			"GET",
			"/users",
			null,
			userOptions,
		);

		const userRole = assignRole(
			userData.data.users[0],
			storedHostName || "",
		);

		useUserStore.getState().setUser({
			firstName: firstName,
			lastName: lastName,
			role: userRole,
			uuid: userData.data.users[0].uuid,
		});

		const newUser: User = {
			firstName: firstName,
			lastName: lastName,
			isUserLoggedIn: true,
			isAdmin: userData.data.users[0]._isAdmin,
			isSupervisor: userData.data.users[0]._isSupervisor,
			uuid: userData.data.users[0].uuid,
		};
		setUser(newUser); // Save the user information in the state and local storage

		return true; // Login is successful
	} else {
		return false; // Login is unsuccessful
	}
}

export async function validateEmailDomain(email: string) {
	const domain = email.split("@")[1];
	const payload = null;

	//Getting the current url and splitting it into protocol and hostname
	const protocol = window.location.protocol;
	const hostname = window.location.hostname;
	const domainName = protocol + "//" + hostname;

	let environment = import.meta.env.MODE;
	if (environment === "development") {
		environment = "DEV";
	} else if (environment === "production") {
		environment = "PRODUCTION";
	} else if (environment === "alpha") {
		environment = "ALPHA";
	}

	const response = await togaApiRequest("GET", "/domains", payload, {
		fields: ["domain"],
		join: [
			{
				ClientEmailDomains: {
					"ClientEmailDomains.clientId": "Domains.clientId",
				},
			},
			{
				["Domains@SelfDomains"]: {
					"SelfDomains.appId": "Domains.appId",
				},
			},
			{
				Environments: {
					"Domains.environmentId": "Environments.id",
				},
			},
		],
		where: {
			and: [
				{
					"ClientEmailDomains.domain": {
						like: domain,
					},
				},
				{
					"Environments.slug": {
						like: environment,
					},
				},
				{
					"SelfDomains.domain": {
						like: domainName,
					},
				},
			],
		},
	} as OptionsType);

	if (response.isSuccess) {
		const domain = response.data.domains[0].domain;
		if (environment === "DEV") {
			return domain + ":5173/login";
		}
		return domain;
	} else {
		return false;
	}
}

export async function getClientUrl(uuid: string) {
	//Getting the current url and splitting it into protocol and hostname
	const protocol = window.location.protocol;
	const hostname = window.location.hostname;
	const payload = null;
	let environment = import.meta.env.MODE;
	if (environment === "development") {
		environment = "DEV";
	} else if (environment === "production") {
		environment = "PRODUCTION";
	} else if (environment === "alpha") {
		environment = "ALPHA";
	}

	const response = await togaApiRequest("GET", "/domains", payload, {
		fields: ["domain"],
		join: [
			{
				Clients: {
					"Clients.id": "Domains.clientId",
				},
			},
			{
				Environments: {
					"Domains.environmentId": "Environments.id",
				},
			},
		],
		where: {
			and: [
				{
					"Clients.uuid": {
						like: uuid,
					},
				},
				{
					"Environments.slug": {
						like: environment,
					},
				},
			],
		},
	} as OptionsType);

	if (response.isSuccess) {
		const domain = response.data.domains[0].domain;
		if (environment === "DEV") {
			return domain + ":5173";
		}
		return domain;
	} else {
		return false;
	}
}
